import { Action, createAction, handleActions } from "redux-actions";
import { GetUsuarioResponse, GetUsuariosResponse, Usuario } from "~api/types";
import { Services, TakeValue } from "./types";

interface GetUsuariosCompletedPayload {
  usuarios: Usuario[];
  totalRegistros: number;
  isAnOldRequest?: boolean;
}

interface CurrentUsuarioPayload {
  currentUsuario: GetUsuarioResponse | null;
}

interface SetUsuariosPayload {
  value: Usuario[] | null;
}

interface StateUsuarioChangedPayload {
  id: string;
  activo: boolean;
}

interface UsuariosState {
  isLoading: boolean;
  failed: boolean;
  currentUsuario: GetUsuarioResponse | null;
  usuarios: Usuario[] | null;
  totalRegistros: number;
}

const setInitialState = createAction("SET_USUARIOS_INITIAL_STATE");

const setCurrentUsuario = createAction(
  "SET_CURRENT_USUARIO",
  (currentUsuario: GetUsuarioResponse | null): CurrentUsuarioPayload => ({
    currentUsuario,
  })
);

const setUsuarios = createAction(
  "SET_USUARIOS",
  (value: Usuario[] | null): SetUsuariosPayload => ({ value })
);

const getUsuariosInited = createAction("OBTENER_USUARIOS_INITED");

const getUsuariosCompleted = createAction(
  "OBTENER_USUARIOS_COMPLETED",
  (
    response: GetUsuariosResponse | null | undefined,
    isAnOldRequest?: boolean
  ): GetUsuariosCompletedPayload => {
    let usuarios: Usuario[] = [];
    let totalRegistros = 0;

    if (response != null) {
      usuarios = response.usuarios;
      totalRegistros = response.totalRegistros;
    }

    return { usuarios, totalRegistros, isAnOldRequest };
  }
);
const getUsuariosFailed = createAction(
  "OBTENER_USUARIOS_FAILED",
  (error: unknown) => ({
    error,
  })
);

const getUsuarios = (
  skip: number,
  take: TakeValue,
  searchCriteria: string,
  getOnlyActivos = true
) => {
  return async (
    dispatch: (action: any) => void,
    getState: () => any,
    services: Services
  ) => {
    try {
      dispatch(getUsuariosInited());

      const data = {
        skip: skip,
        take: Number(take),
        searchCriteria: searchCriteria,
        getOnlyActivos: getOnlyActivos,
      };
      const response = await services.api.usuarios().getUsuarios(data);

      dispatch(getUsuariosCompleted(response));
    } catch (error) {
      console.error(error);
      dispatch(getUsuariosFailed(error));
    }
  };
};

const putUsuariosInited = createAction("ACTUALIZAR_USUARIOS_INITED");

const putUsuariosCompleted = createAction("ACTUALIZAR_USUARIOS_COMPLETED");
const putUsuariosFailed = createAction(
  "ACTUALIZAR_USUARIOS_FAILED",
  (error: unknown) => ({
    error,
  })
);

const putUsuarios = (
  id: string,
  roles: string[],
  oficinaId: string | undefined,
  activo: boolean
) => {
  return async (
    dispatch: (action: any) => void,
    getState: () => any,
    services: Services
  ) => {
    try {
      dispatch(putUsuariosInited());

      const data = { id, roles, oficinaId, activo };

      const response = await services.api.usuarios().putUsuarios(data);

      dispatch(putUsuariosCompleted(response));

      //reload user list
      const state = getState().filtrosUsuariosReducer;

      dispatch(
        getUsuarios(
          state.take * state.page,
          state.take,
          state.searchCriteria,
          state.getOnlyActivos
        )
      );
    } catch (error) {
      console.error(error);
      dispatch(putUsuariosFailed(error));
    }
  };
};

const stateUsuarioChanged = createAction(
  "STATE_USUARIO_CHANGED",
  (id: string, activo: boolean): StateUsuarioChangedPayload => ({ id, activo })
);

const changeStateUsuario = (
  id: string,
  roles: string[],
  oficinaId: string | undefined,
  activo: boolean
) => {
  return async (
    dispatch: (action: any) => void,
    getState: () => any,
    services: Services
  ) => {
    try {
      const data = { id, roles, oficinaId, activo };
      dispatch(stateUsuarioChanged(id, activo));
      await services.api.usuarios().putUsuarios(data);
    } catch (error) {
      console.error(error);
      //if error, force to reload
      const state = getState().filtrosUsuariosReducer;
      dispatch(
        getUsuarios(
          state.take * state.page,
          state.take,
          state.searchCriteria,
          state.getOnlyActivos
        )
      );
    }
  };
};

const getUsuarioInited = createAction("OBTENER_USUARIO_INITED");

const getUsuarioCompleted = createAction(
  "OBTENER_USUARIO_COMPLETED",
  (currentUsuario: GetUsuarioResponse): CurrentUsuarioPayload => ({
    currentUsuario,
  })
);
const getUsuarioFailed = createAction(
  "OBTENER_USUARIO_FAILED",
  (error: unknown) => ({
    error,
  })
);

const getUsuario = (id: string) => {
  return async (
    dispatch: (action: any) => void,
    getState: () => any,
    services: Services
  ) => {
    try {
      dispatch(getUsuarioInited());

      const response = await services.api.usuarios().getUsuario(id);

      dispatch(getUsuarioCompleted(response));
    } catch (error) {
      console.error(error);
      dispatch(getUsuarioFailed(error));
    }
  };
};

const usuariosInitialState: UsuariosState = {
  isLoading: false,
  failed: false,
  currentUsuario: null,
  usuarios: null,
  totalRegistros: 0,
};

const usuariosReducer = handleActions<UsuariosState, any>(
  {
    [setInitialState.toString()]: (): UsuariosState => {
      return {
        ...usuariosInitialState,
        usuarios: null,
      };
    },
    [setCurrentUsuario.toString()]: (
      state: UsuariosState,
      action: Action<CurrentUsuarioPayload>
    ): UsuariosState => {
      return {
        ...state,
        currentUsuario: action.payload.currentUsuario,
      };
    },
    [getUsuariosInited.toString()]: (state: UsuariosState): UsuariosState => {
      return {
        ...state,
        totalRegistros: 0,
        isLoading: true,
      };
    },
    [getUsuariosCompleted.toString()]: (
      state: UsuariosState,
      action: Action<GetUsuariosCompletedPayload>
    ): UsuariosState => {
      return {
        ...state,
        usuarios: action.payload.usuarios,
        totalRegistros: action.payload.totalRegistros,
        isLoading: false,
        failed: false,
      };
    },
    [getUsuariosFailed.toString()]: (state: UsuariosState): UsuariosState => {
      return {
        ...state,
        isLoading: false,
        failed: true,
      };
    },

    [putUsuariosInited.toString()]: (state: UsuariosState): UsuariosState => {
      return {
        ...state,
        isLoading: true,
      };
    },
    [putUsuariosCompleted.toString()]: (
      state: UsuariosState
    ): UsuariosState => {
      return {
        ...state,
      };
    },
    [putUsuariosFailed.toString()]: (state: UsuariosState): UsuariosState => {
      return {
        ...state,
        isLoading: false,
      };
    },

    [getUsuarioInited.toString()]: (state: UsuariosState): UsuariosState => {
      return {
        ...state,
        currentUsuario: null,
      };
    },
    [getUsuarioCompleted.toString()]: (
      state: UsuariosState,
      action: Action<CurrentUsuarioPayload>
    ): UsuariosState => {
      return {
        ...state,
        currentUsuario: action.payload.currentUsuario,
        failed: false,
      };
    },
    [getUsuarioFailed.toString()]: (state: UsuariosState): UsuariosState => {
      return {
        ...state,
        failed: true,
      };
    },
    [setUsuarios.toString()]: (
      state: UsuariosState,
      action: Action<SetUsuariosPayload>
    ): UsuariosState => {
      return {
        ...state,
        usuarios: action.payload.value,
      };
    },
    [stateUsuarioChanged.toString()]: (
      state: UsuariosState,
      action: Action<StateUsuarioChangedPayload>
    ): UsuariosState => {
      return {
        ...state,
        usuarios: state.usuarios
          ? state.usuarios.map((usuario) =>
              usuario.id === action.payload.id
                ? { ...usuario, activo: action.payload.activo }
                : usuario
            )
          : state.usuarios,
      };
    },
  },
  usuariosInitialState
);

export { usuariosReducer };
export {
  getUsuarios,
  setInitialState,
  setUsuarios,
  setCurrentUsuario,
  putUsuarios,
  getUsuario,
  changeStateUsuario,
};
